import { useCallback, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setActionType,
  setCustomMessage,
  setLabel,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import { selectCurrentUser, updateCurrentUser } from "features/auth/auth-slice";
import { useUpdateUserMutation } from "services/users";

export const DND_DESCRIPTION =
  "Incoming calls skip your devices entirely (straight to your call forwarding or voicemail) until you turn this off.";

/**
 * Do Not Disturb (ENG-1858 / ENG-1999), read from and written to the signed-in
 * user in the auth slice. Suno stores `voice_dnd_enabled` on the user and the
 * backend writes it through to DialStack's server-side `do_not_disturb` — the
 * browser SDK cannot set presence. Both surfaces that expose the switch (the
 * softphone widget's status dot and Settings → My Phone) share this hook, so
 * flipping it in one place shows in the other without a reload.
 */
export default function useVoiceDnd() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const [updateUser, { isLoading: isSaving }] = useUpdateUserMutation();

  const enabled = Boolean(currentUser?.voice_dnd_enabled);
  const userId = currentUser?.id;

  // Optimistic: the dot and the switch flip at once, and flip back if the
  // save fails, so neither surface waits on the round trip. Only the newest
  // save gets to confirm or roll back: two quick flips can answer out of
  // order, and a stale reply must not undo the later choice.
  const latestSave = useRef(0);
  const setEnabled = useCallback(
    async (next: boolean): Promise<boolean> => {
      if (!userId || userId === -1) return false;
      latestSave.current += 1;
      const save = latestSave.current;
      dispatch(updateCurrentUser({ voice_dnd_enabled: next }));
      try {
        await updateUser({ id: userId, voice_dnd_enabled: next }).unwrap();
        if (save !== latestSave.current) return true;
        dispatch(setLabel("Voice Preferences"));
        dispatch(
          setCustomMessage(
            next ? "Do not disturb turned on." : "Do not disturb turned off."
          )
        );
        dispatch(setActionType("update"));
        dispatch(setOpen(true));
        return true;
      } catch {
        if (save !== latestSave.current) return false;
        dispatch(updateCurrentUser({ voice_dnd_enabled: !next }));
        dispatch(
          setCustomMessage("Could not save do not disturb. Please try again.")
        );
        dispatch(setOpen(true));
        return false;
      }
    },
    [dispatch, updateUser, userId]
  );

  return { enabled, setEnabled, isSaving };
}
